// 사이드바 — 5섹션 통합 (U40 기준).
//  S1) 고정 (Pinned)    : pinned=true 항목 최상단, 드래그 순서 변경 (U40)
//  S2) 크리덴셜 + 트리  : 그룹 트리 (접기·펼치기, localStorage 영속화, 자식 카운트)
//  S3) 스마트 폴더      : 5개 동적 폴더 (U38)
//  S4) 태그             : 빈도 기반 태그 클라우드 (U39)
//  S5) 설정             : 클릭 시 하위 메뉴 펼침 (보안 / 계정 / 접속 로그)
//
// "전체 보기" / "루트(미분류)" 항목은 S2 트리 상단에 항상 표시.
// URL 쿼리 ?group=<id> 와 동기화.
// 그룹 추가 / 항목 추가 / 그룹 삭제는 상위 페이지에서 모달로 열린다 — 여기선 CustomEvent로 호출.

// 아이콘: lucide-react (U34 아이콘 시스템 도입)
// - 태그 관리: Tag
// - 전체 보기: FolderOpen
// - 설정 토글: ChevronRight/ChevronDown (rotate 트랜지션)
// - 설정 하위 메뉴: Shield(보안), User(계정), ScrollText(접속 로그)
// S1: PinnedList (U40) — 최상단 고정 섹션
// S3: SmartFolderList (U38) — Pinned/Tree 다음, 설정 이전

import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Folder, FolderOpen, ScrollText, Shield, Tag, User } from 'lucide-react';
import { useGroupsTree } from '../hooks/useGroupsTree';
import { useCreateGroup } from '../hooks/useGroups';
import { useToast } from '../ui/Toast';
import type { GroupKind } from '../api/types';
import type { GroupTreeNode } from '../api/groupsTree';
import { GroupTreeNodeComponent } from './GroupTreeNode';
import { SmartFolderList } from './Sidebar/SmartFolderList';
import { TagCloud } from './Sidebar/TagCloud';
import { PinnedList } from './Sidebar/PinnedList';
import { InlineEditable } from './InlineEditable';
import { Tooltip } from '../ui/Tooltip';
import {
  getExpandedIds,
  setExpanded,
  expandAncestors,
} from '../utils/treeExpansion';

/**
 * 사이드바가 페이지에 "이 작업 열어달라"고 알릴 때 쓰는 커스텀 이벤트.
 * 각 페이지가 mount 시 listen 등록, unmount 시 해제.
 */
export type SidebarIntent =
  | { type: 'create-group'; kind: GroupKind }
  | { type: 'delete-group'; kind: GroupKind; groupId: number; groupName: string }
  | { type: 'create-item'; kind: GroupKind; groupId: number };

const EVENT_NAME = 'locker:sidebar-intent';

export function emitSidebarIntent(intent: SidebarIntent): void {
  window.dispatchEvent(new CustomEvent<SidebarIntent>(EVENT_NAME, { detail: intent }));
}

export function useSidebarIntent(listener: (intent: SidebarIntent) => void): void {
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<SidebarIntent>;
      if (ce.detail) listener(ce.detail);
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, [listener]);
}

/**
 * 검색 결과 기반 사이드바 자동 펼침 이벤트.
 * Explorer 등에서 매칭된 group_id 배열을 사이드바에 전달.
 */
const EXPAND_EVENT = 'locker:sidebar-expand-groups';

export function emitExpandGroups(groupIds: number[]): void {
  window.dispatchEvent(new CustomEvent<number[]>(EXPAND_EVENT, { detail: groupIds }));
}

// ─── Sidebar ──────────────────────────────────────

export function Sidebar() {
  const loc = useLocation();
  const settingsOpen = loc.pathname.startsWith('/settings');

  return (
    <aside className="sidebar">
      {/* S1: Pinned — U40 신규, 최상단 고정 항목 */}
      <PinnedList />
      {/* S2: 크리덴셜 + 그룹 트리 */}
      <CredentialTreeSection />
      {/* S3: Smart Folders — U38 */}
      <SmartFolderList />
      {/* S4: Tag Cloud — U39 */}
      <TagCloud />
      {/* S5: 설정 */}
      <SettingsSection openInitially={settingsOpen} />
    </aside>
  );
}

// ─── 크리덴셜 트리 섹션 ─────────────────────────────

/**
 * 특정 그룹 ID의 모든 조상 ID를 forest에서 찾아 반환한다.
 * Phase 2 검색 연동(U31)에서 매칭 그룹의 조상 자동 펼침에 사용.
 */
export function findAncestorIds(nodes: GroupTreeNode[], targetId: number): number[] {
  for (const node of nodes) {
    if (node.id === targetId) return [];
    const childResult = findAncestorIds(node.children, targetId);
    if (childResult !== null) {
      return [node.id, ...childResult];
    }
  }
  // 찾지 못한 경우 null 반환 (타입 단순화를 위해 빈 배열 구분 필요 — 내부 헬퍼)
  return null as unknown as number[];
}

/**
 * 주어진 그룹 ID 배열의 모든 조상을 localStorage에 자동 펼침.
 * 검색 결과와 연동 시 사용하는 helper (U31에서 호출).
 */
export function expandGroupsByIds(nodes: GroupTreeNode[], ids: number[]): void {
  const ancestorIds: number[] = [];
  for (const id of ids) {
    const ancestors = findAncestorIds(nodes, id);
    if (ancestors) ancestorIds.push(...ancestors);
  }
  if (ancestorIds.length > 0) {
    expandAncestors(ancestorIds);
  }
}

function CredentialTreeSection() {
  const { data: tree = [], isLoading } = useGroupsTree('credential');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 루트 그룹 인라인 추가 상태
  const [isAddingRoot, setIsAddingRoot] = useState(false);
  const createGroup = useCreateGroup();
  const { push: toastPush } = useToast();

  // URL ?group=<id> 파라미터에서 현재 선택 그룹 ID 읽기
  const selectedIdParam = searchParams.get('group');
  const selectedId = selectedIdParam ? parseInt(selectedIdParam, 10) : null;

  // localStorage에서 초기 확장 상태 로드
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => getExpandedIds());

  // 현재 선택된 그룹의 조상을 자동 펼침 (URL 변경 시)
  useEffect(() => {
    if (selectedId !== null && tree.length > 0) {
      const ancestors = findAncestorIds(tree, selectedId);
      if (ancestors && ancestors.length > 0) {
        expandAncestors(ancestors);
        setExpandedIds(getExpandedIds());
      }
    }
  }, [selectedId, tree]);

  // 검색 결과 기반 자동 펼침 — Explorer에서 emitExpandGroups 호출 시 트리거
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<number[]>;
      if (!ce.detail || ce.detail.length === 0 || tree.length === 0) return;
      expandGroupsByIds(tree, ce.detail);
      setExpandedIds(getExpandedIds());
    };
    window.addEventListener(EXPAND_EVENT, handler);
    return () => window.removeEventListener(EXPAND_EVENT, handler);
  // tree가 바뀔 때마다 리스너 재등록 (최신 tree 참조)
  }, [tree]);

  // 접기/펼치기 토글
  const handleToggle = useCallback((id: number) => {
    const next = !expandedIds.has(id);
    setExpanded(id, next);
    setExpandedIds(getExpandedIds());
  }, [expandedIds]);

  // 그룹 선택 — Explorer URL 쿼리 갱신 (U32: Explorer가 기본 화면)
  const handleSelect = useCallback((id: number) => {
    navigate(`/?group=${id}`);
  }, [navigate]);

  // 루트 그룹 인라인 저장
  const handleSaveRootGroup = useCallback(async (name: string) => {
    await createGroup.mutateAsync({ name, kind: 'credential', parentId: null });
    setIsAddingRoot(false);
    toastPush({ title: `'${name}' 그룹이 추가되었습니다`, variant: 'success', duration: 2000 });
  }, [createGroup, toastPush]);

  const handleCancelRootGroup = useCallback(() => {
    setIsAddingRoot(false);
  }, []);

  return (
    <section className="sidebar__section">
      {/* 상단 고정 항목: 전체 보기 / 태그 관리 */}
      {/* U32: /explorer 링크 제거 — Explorer가 루트 기본 화면 */}
      {/* "루트(미분류)" 버튼 제거 — SmartFolders의 "미분류" 항목과 중복 */}
      <ul className="sidebar__list sidebar__list--top">
        <li>
          {/* 태그 관리 링크 — 🏷️ → Tag 아이콘 (U34) */}
          <NavLink to="/tags" className="sidebar__nav-link">
            <Tag size={14} aria-hidden />
            태그 관리
          </NavLink>
        </li>
        <li>
          {/* 전체 보기 — 📂 → FolderOpen 아이콘 (U34) */}
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `sidebar__nav-link${isActive && !selectedId ? ' active' : ''}`
            }
          >
            <FolderOpen size={14} aria-hidden />
            전체 보기
          </NavLink>
        </li>
      </ul>

      {/* 섹션 헤더 */}
      <div className="sidebar__heading">
        <span>크리덴셜</span>
        <Tooltip content="루트 그룹 추가" side="right" sideOffset={6}>
          <button
            type="button"
            className="btn btn--tiny"
            onClick={() => setIsAddingRoot(true)}
            aria-label="크리덴셜 루트 그룹 추가"
            disabled={isAddingRoot}
          >
            + 그룹 추가
          </button>
        </Tooltip>
      </div>

      {/* 트리 렌더 */}
      {isLoading ? (
        <div className="muted sidebar__hint">불러오는 중…</div>
      ) : tree.length === 0 && !isAddingRoot ? (
        <div className="muted sidebar__hint credential-tree__empty">
          그룹이 없어요.{' '}
          <button
            type="button"
            className="sidebar__hint-action"
            onClick={() => setIsAddingRoot(true)}
          >
            + 그룹 추가
          </button>
          로 시작하세요.
        </div>
      ) : (
        <ul className="sidebar__list sidebar__tree">
          {/* 루트 그룹 인라인 추가 input */}
          {isAddingRoot && (
            <li className="tree-node__item tree-node__item--adding">
              <div className="tree-node__row" style={{ paddingLeft: '8px' }}>
                <span className="tree-node__folder-icon" aria-hidden>
                  <Folder size={13} />
                </span>
                <InlineEditable
                  value=""
                  onSave={handleSaveRootGroup}
                  onCancel={handleCancelRootGroup}
                  validate={(v) => v.trim().length > 0}
                  autoFocus
                  placeholder="새 그룹 이름 (Esc로 취소)"
                  className="tree-node__inline-edit"
                />
              </div>
            </li>
          )}
          {tree.map((node) => (
            <GroupTreeNodeComponent
              key={node.id}
              node={node}
              depth={0}
              expandedIds={expandedIds}
              onToggle={handleToggle}
              selectedId={selectedId}
              onSelect={handleSelect}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── 설정 섹션 (하위 메뉴 접힘) ────────────────

function SettingsSection({ openInitially }: { openInitially: boolean }) {
  const [open, setOpen] = useState(openInitially);

  // 라우트가 /settings/로 바뀌면 자동 펼침
  const loc = useLocation();
  useMemo(() => {
    if (loc.pathname.startsWith('/settings')) setOpen(true);
  }, [loc.pathname]);

  return (
    <section className="sidebar__section">
      <button
        type="button"
        className="sidebar__heading sidebar__heading--button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>설정</span>
        {/* ▸/▾ 텍스트 → ChevronRight/ChevronDown 아이콘 (U34) */}
        {open
          ? <ChevronDown size={14} aria-hidden className="sidebar__chevron" />
          : <ChevronRight size={14} aria-hidden className="sidebar__chevron" />
        }
      </button>
      {open && (
        <ul className="sidebar__list">
          {/* 설정 하위 메뉴 아이콘 (U34) */}
          <li><NavLink to="/settings/security" className="sidebar__nav-link"><Shield size={14} aria-hidden />보안</NavLink></li>
          <li><NavLink to="/settings/account" className="sidebar__nav-link"><User size={14} aria-hidden />계정</NavLink></li>
          <li><NavLink to="/settings/access-logs" className="sidebar__nav-link"><ScrollText size={14} aria-hidden />접속 로그</NavLink></li>
        </ul>
      )}
    </section>
  );
}
